/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */
import React from 'react';
import { makeStyles } from '@material-ui/styles';
import { useUIDSeed } from 'react-uid';
import {
    shape, string,
} from 'prop-types';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { trackEvent as trackEventActions } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import DesktopMobile from '../../../../helpers/DesktopMobile/DesktopMobile';

const useStyles = makeStyles((theme) => ({
    mainBanner: {
        width: '100%',
    },
    mainImageBanner: {
        position: 'relative',
        width: '100%',
        margin: '0 auto',
        display: 'block',
        '& img': {
            display: 'block',
            width: '100%',
            margin: '0 auto',
        },
    },
    mainBannerContentContainer: {
        display: 'flex',
        height: 'inherit',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '0 60px 0 60px',
        [theme.breakpoints.down(769)]: {
            marginLeft: '20px',
            marginRight: '20px',
            justifyContent: 'center !important', // need some way to over-write cms inline style
        },
        [theme.breakpoints.down(600)]: {
            // paddingTop: '20px',
            alignItems: 'center', // we need to handle this from cs
        },
    },
    mainBannerContent: ({ maxBoxWidth }) => ({
        width: `${maxBoxWidth}px` || '650px',
        padding: '20px',
        backgroundColor: '#FFF',
    }),
    innerBannerBorder: ({ boxInnerPadding }) => ({
        padding: boxInnerPadding || '0',
        border: '1px solid #000',
        [theme.breakpoints.down(769)]: {
            padding: '20px',
        },
    }),
    innerBanner: ({ boxInnerPadding }) => ({
        padding: boxInnerPadding || '0',
        [theme.breakpoints.down(769)]: {
            padding: '0px',
        },
    }),
    title: {
        position: 'relative',
        textAlign: 'center',
        '& h1, h2, & h3, & h4, & h5, & h6': {
            fontSize: '16px',
            textTransform: 'uppercase',
            lineHeight: '16px',
            letterSpacing: '1.47px',
            position: 'relative',
            display: 'inline-block',
            zIndex: 9,
            background: '#FFF',
            padding: '0 10px',
            [theme.breakpoints.down(600)]: {
                fontSize: '14px',
                marginBottom: '7px',
            },
        },
    },
    lineThrough: {
        '&::before': {
            content: '""',
            background: '#000',
            width: '100%',
            height: '1px',
            position: 'absolute',
            right: 0,
            left: 0,
            top: '50%',
            zIndex: 1,
        },
    },
    bannerHeadline: ({ headlineFontSize, textAlignment }) => ({
        marginBottom: '7px',
        '& p': {
            textAlign: textAlignment || 'center',
        },
        '& h1,& h2,& h3,& h4,& h5,& h6': {
            margin: '0',
            fontFamily: 'ivypresto-text, PlayfairDisplay, serif',
            fontSize: headlineFontSize || '36px',
            lineHeight: '46px',
            textAlign: textAlignment || 'center',
            [theme.breakpoints.down(769)]: {
                lineHeight: '42px',
                fontSize: '32px',
            },
        },
    }),
    bannerSubHeadline: ({ textAlignment, hyperlinkColors }) => ({
        textAlign: textAlignment || 'center',
        marginBottom: '20px',
        '& h1,& h2,& h3,& h4,& h5,& h6': {
            margin: 0,
            fontSize: '16px',
            textTransform: 'uppercase',
            letterSpacing: '1.47px',
            lineHeight: '18px',
            marginBottom: '15px',
            [theme.breakpoints.down(600)]: {
                lineHeight: '18px',
                fontSize: '14px',
                marginBottom: '20px',
            },
        },
        '& p': {
            fontSize: '16px',
            lineHeight: '20px',
            margin: 0,
        },
        '& ul': {
            paddingLeft: '20px',
        },
        '& a': {
            color: hyperlinkColors || '#000',
        },
    }),
    mainBannerDesktop: ({ dtBannerHeight }) => ({
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        height: `${dtBannerHeight}px` || '400px',
        width: '100%',
        margin: '0 auto',
    }),
}));

const flexJustifyDirection = (direction) => {
    switch (direction) {
        case 'left':
            return 'flex-start';

        case 'center':
            return 'center';

        case 'right':
            return 'flex-end';

        default:
            return 'center';
    }
};

const TemplateMultiSubMainBanner = ({
    item,
}) => {
    if (!item) {
        return null;
    }
    const seed = useUIDSeed();
    const mainBannerData = item?.main_banner;
    const link = mainBannerData?.link?.href || '';
    const url = mainBannerData?.desktop_image?.url || '';
    const mobileUrl = mainBannerData?.mobile_image?.url || '';
    const description = mainBannerData?.desktop_image?.description || 'John Tesh Banner';
    const bannerTitle = mainBannerData?.banner_content?.title || '';
    const bannerHeadline = mainBannerData?.banner_content?.headline || '';
    const bannerSubHeadline = mainBannerData?.banner_content?.sub_headline || '';
    const ctaButtonBorder = mainBannerData?.banner_content?.cta_button_border || '';
    const enableLineThroughCta = mainBannerData?.banner_content?.enable_line_through_cta || false;
    const enableTitleLineThrough = mainBannerData?.banner_content?.enable_title_line_through || false;
    const mobBannerHeight = mainBannerData?.mobile_banner_height;
    const classes = useStyles({
        dtBannerHeight: mainBannerData?.desktop_banner_height,
        maxBoxWidth: mainBannerData?.banner_content?.max_box_width,
        textAlignment: flexJustifyDirection(mainBannerData?.banner_content?.text_alignment),
        boxInnerPadding: mainBannerData?.banner_content?.box_inner_padding,
        headlineFontSize: mainBannerData?.banner_content?.headline_font_size,
        hyperlinkColors: mainBannerData?.banner_content?.sub_headline_links_font_color,
    });

    // show border line on content
    const showBorder = mainBannerData?.banner_content?.box_border;

    // for Banner Button
    const ctaButton = mainBannerData?.banner_content?.cta_button_link?.title || '';
    const ctaButtonLink = mainBannerData?.banner_content?.cta_button_link?.href || '';
    const ctaButtonColor = mainBannerData?.banner_content?.cta_button_text_color || '';

    // for styling bannerHeadline
    const headerbannerStyle = {
        color: mainBannerData?.banner_content?.headline_font_color || '#000000',
    };
    // for styling bannerSubHeadline
    const headerSubHeadlineStyle = {
        color: mainBannerData?.banner_content?.sub_headline_font_color || '#000000',
        textAlign: 'flex-start',
    };

    const bannerHtmlContent = () => (
        <div className={classes.mainBannerContentContainer} style={{ justifyContent: flexJustifyDirection(mainBannerData?.banner_content?.copy_alignment) }}>
            <div className={classes.mainBannerContent}>
                <div className={showBorder ? classes.innerBannerBorder : classes.innerBanner}>
                    {bannerTitle && <div className={`${classes.title} ${enableTitleLineThrough && classes.lineThrough}`}><h2><strong>{bannerTitle}</strong></h2></div>}
                    <div style={headerbannerStyle}>
                        <ReactMarkdown className={classes.bannerHeadline} source={bannerHeadline} />
                    </div>
                    <div id="text-desc" style={headerSubHeadlineStyle}>
                        <ReactMarkdown className={classes.bannerSubHeadline} source={bannerSubHeadline} />
                    </div>
                    {ctaButton && (
                        ctaButtonLink ? <Link to={ctaButtonLink} target="_blank" style={{ padding: '10px 15px', border: ctaButtonBorder, color: ctaButtonColor }} className={`${classes.title} ${enableLineThroughCta && classes.lineThrough}`}><h2><strong>{ctaButton}</strong></h2></Link>
                            : <div style={{ border: ctaButtonBorder, color: ctaButtonColor }} className={`${classes.title} ${enableLineThroughCta && classes.lineThrough}`}><h2><strong>{ctaButton}</strong></h2></div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            {
                // if Main Banner block has no href/url and has no headline display an image
                !link && (!bannerHeadline) && (
                    <DesktopMobile
                        breakpoint={600}
                        desktop={() => <img src={url} alt={description} className={classes.mainImageBanner} key={seed(item)} />}
                        mobile={() => <img src={mobileUrl} alt={description} className={classes.mainImageBanner} key={seed(item)} />}
                    />
                )
            }
            {
                // if Main Banner block has an href/url make the image a link
                link && (
                    <Link key={seed(item)} to={link} className={classes.mainImageBanner}>
                        <DesktopMobile
                            breakpoint={600}
                            desktop={() => <img src={url} alt={description} className={classes.mainImageBanner} />}
                            mobile={() => <img src={mobileUrl} alt={description} className={classes.mainImageBanner} />}
                        />
                    </Link>
                )
            }

            {   // if Main Banner block has a title or headline use hero image and content block
                (bannerTitle || bannerHeadline) && (
                    <DesktopMobile
                        breakpoint={600}
                        desktop={() => (
                            <div
                                key={seed(item)}
                                className={classes.mainBannerDesktop}
                                style={{
                                    backgroundImage: `url('${url}')`,
                                }}
                            >
                                {bannerHtmlContent()}
                            </div>
                        )}
                        mobile={() => (
                            <div
                                key={seed(item)}
                                className={classes.mainBanner}
                                style={{
                                    backgroundImage: `url('${mobileUrl}')`,
                                    backgroundSize: 'cover',
                                    backgroundRepeat: 'no-repeat',
                                    height: `${mobBannerHeight}px` || '380px',
                                }}
                            >
                                {bannerHtmlContent()}
                            </div>
                        )}
                    />
                )
            }
        </>
    );
};
TemplateMultiSubMainBanner.propTypes = {
    item: shape({
        banner_content: shape({
            cta_button_background_color: string,
            cta_button_text_color: string,
            headline: string,
            headline_font_color: string,
            sub_headline: string,
            sub_headline_font_color: string,
        }),
        desktop_image: shape({
            title: string,
            url: string,
        }),
        link: shape({
            title: string,
            href: string,
        }),
    }),

};
TemplateMultiSubMainBanner.defaultProps = {
    item: {},
};

const mapDispatchToProps = (dispatch) => ({
    trackEvent: bindActionCreators(trackEventActions, dispatch),
});

const enhance = compose(
    connect(null, mapDispatchToProps),
);

export default enhance(TemplateMultiSubMainBanner);
